import { isToken, getTokenPath, walkTokens, resolveToken, resolveTokenShallow } from './token';
export function resolveShallowToken(token, overrideMap, isConditionTrue) {
    const result = resolveTokenShallow(token, overrideMap, isConditionTrue);
    if (isToken(result)) {
        return {
            isAlias: true,
            path: getTokenPath(result)
        };
    }
    return result;
}
function setPath(obj, path, val) {
    const parts = path.split('.');
    let baseObj = obj;
    parts.forEach((part, idx)=>{
        if (typeof baseObj !== 'object') {
            throw new Error(`${part} - ${baseObj[part]} already exists`);
        } else if (!baseObj[part]) {
            if (idx === parts.length - 1) {
                baseObj[part] = val;
            } else {
                baseObj[part] = {};
            }
        }
        // nosemgrep: prototype-pollution-loop
        baseObj = baseObj[part];
    });
    return obj;
}
function guessType(path, val) {
    if (val !== undefined && val !== null) {
        if (typeof val === 'string') {
            if (val[0] === '#' || val.startsWith('rgb')) {
                return 'color';
            }
            if (val.length > 8 && (val.indexOf('#') > 0 || val.indexOf('rgb') > 0)) {
                return 'shadow';
            }
            if (val.indexOf('px') > 0 || val.indexOf('rem') > 0) {
                return 'dimension';
            }
            if (val.indexOf('%') > 0) {
                return 'number';
            }
            if (path.indexOf('weight') > 0) {
                return 'fontWeight';
            }
            if (path.indexOf('typeface') > 0) {
                return 'fontFamily';
            }
            if (val === '0' && path.indexOf('small') > 0) {
                return 'dimension';
            }
        }
        if (typeof val === 'number') {
            return 'number';
        }
        return 'string';
    }
}
// Helper to format W3C token references
function toW3CAliasValue(alias) {
    return `{${alias.path}}`;
}
// Formats the token value into a W3C dimension string
function toW3CDimension(token, overrideMap) {
    const value = resolveShallowToken(token, overrideMap);
    if (typeof value === 'number') {
        return `${value}px`;
    }
    if (typeof value === 'string') {
        return value;
    }
    if (value.isAlias) {
        return toW3CAliasValue(value);
    }
    throw new Error(`Unrecognized dimension token type ${typeof value}`);
}
// Formats the token value into a W3C fontFamily string or array
function toW3CFontFamily(shallow) {
    let result = shallow;
    if (typeof shallow === 'string') {
        result = shallow.split(',');
    }
    if (Array.isArray(result) && result.length > 0 && typeof result[0] === 'string') {
        return result.map((v)=>v.trim()).map((v)=>v.replace(/(^"|"$|^'|'$)/g, ''));
    }
    if (result.isAlias) {
        return toW3CAliasValue(result);
    }
    throw new Error(`Unrecognized fontFamily token type '${shallow}'`);
}
function isTypographyToken(t) {
    var _t_family;
    if (t && (t === null || t === void 0 ? void 0 : (_t_family = t.family) === null || _t_family === void 0 ? void 0 : _t_family.typeface) && (t === null || t === void 0 ? void 0 : t.size) && (t === null || t === void 0 ? void 0 : t.weight)) {
        return true;
    }
    return false;
}
// Determines if the given token is a typography token and returns the W3C
// composite token if it is, otherwise returns null
function toW3CTypographyToken(token, overrideMap) {
    if (!isTypographyToken(token)) {
        return null;
    }
    const { family, size, weight } = token;
    const fontFamily = toW3CFontFamily(resolveShallowToken(family.typeface, overrideMap));
    // TODO: Compute the lineHeight as a multiplier from fontSize and original lineHeight
    // The W3C spec expects that `lineHeight` be a multiplier of `fontSize`:
    // https://design-tokens.github.io/community-group/format/#typography
    return {
        fontFamily,
        fontSize: toW3CDimension(size, overrideMap),
        fontWeight: toW3CDimension(weight, overrideMap),
        letterSpacing: '0px',
        lineHeight: 1
    };
}
function parseCssShadow(str) {
    // Mostly copied from css-box-shadow package
    // https://github.com/jxnblk/css-box-shadow/blob/master/index.js
    return str.split(/,(?![^(]*\))/).map((s)=>s.trim()).map((str)=>{
        const parts = str.split(/\s(?![^(]*\))/);
        let color = parts.slice(-1)[0];
        if (color === '0' || /^[0-9]+[a-zA-Z%]+?$/.test(color)) {
            throw new Error("Can't parse shadow without color");
        }
        if (color === 'transparent') {
            color = 'rgba(0,0,0,0)';
        }
        const nums = parts.filter((n)=>n !== color).map((n)=>n === '0' ? '0px' : n).concat(Array(4).fill('0px')); // Pad to ensure non-undefined values
        const [offsetX, offsetY, blur, spread] = nums;
        return {
            color,
            offsetX,
            offsetY,
            blur,
            spread
        };
    });
}
export function toW3CTokenFormat(tokens, overrideMap) {
    const result = {};
    const isDarkModeConditionTrue = (condition)=>condition === '@media (prefers-color-scheme: dark)';
    walkTokens(tokens, (value, path)=>{
        const typographyValue = toW3CTypographyToken(value, overrideMap);
        if (typographyValue) {
            const pathVal = {
                $value: typographyValue,
                $type: 'typography'
            };
            setPath(result, path, pathVal);
        } else if (isToken(value)) {
            const resolvedToken = resolveToken(value, overrideMap);
            const resolvedType = guessType(path, resolvedToken);
            const shallow = resolveShallowToken(value, overrideMap);
            let simpleVal = shallow;
            if (shallow === null || shallow === void 0 ? void 0 : shallow.isAlias) {
                simpleVal = toW3CAliasValue(shallow);
            }
            const pathVal = {
                $value: simpleVal
            };
            if (!shallow || !(shallow === null || shallow === void 0 ? void 0 : shallow.isAlias)) {
                pathVal.$type = guessType(path, simpleVal);
            }
            if (pathVal.$type === 'fontFamily') {
                pathVal.$value = toW3CFontFamily(shallow);
            }
            if (pathVal.$type === 'shadow') {
                pathVal.$value = parseCssShadow(simpleVal);
                const darkModeToken = resolveShallowToken(value, overrideMap, isDarkModeConditionTrue);
                if (darkModeToken && darkModeToken !== simpleVal) {
                    // Assumes only colors differ between light and dark theme shadows
                    pathVal.$extensions = {
                        'org.stripe.darkTheme': parseCssShadow(darkModeToken).map((s)=>s.color)
                    };
                }
            }
            // Set darkTheme for colors.
            if (resolvedType === 'color') {
                const darkModeToken = resolveShallowToken(value, overrideMap, isDarkModeConditionTrue);
                const darkSimpleVal = (darkModeToken === null || darkModeToken === void 0 ? void 0 : darkModeToken.isAlias) ? toW3CAliasValue(darkModeToken) : darkModeToken;
                if (darkSimpleVal && darkSimpleVal !== simpleVal) {
                    pathVal.$extensions = {
                        'org.stripe.darkTheme': darkSimpleVal
                    };
                }
            }
            setPath(result, path, pathVal);
        }
    });
    return result;
}
// The `$value` key, used by the W3C standard, is not compatible with StyleDictionary.
// This replaces `$value` with `value` in the token tree.
function styleDictionaryTransformValueKey(record) {
    if ('$value' in record) {
        record.value = record.$value;
        delete record.$value;
    }
    Object.keys(record).forEach((key)=>{
        if (typeof record[key] === 'object') {
            styleDictionaryTransformValueKey(record[key]);
        }
    });
}
function replaceAliasInValue(value, oldPath, newPath) {
    if (typeof value === 'string') {
        const newValue = value.replace(`{${oldPath}`, `{${newPath}`);
        return newValue;
    } else if (Array.isArray(value)) {
        const newValue = value.map((v)=>{
            if (typeof v === 'string') {
                return v.replace(`{${oldPath}`, `{${newPath}`);
            }
            return v;
        });
        return newValue;
    }
    return value;
}
// Replaces all alias references using the old path in the token tree.
function replaceAliases(record, oldPath, newPath) {
    Object.keys(record).forEach((key)=>{
        if (key === '$value' && typeof record[key] === 'object') {
            const compositeValue = record[key];
            // Composite value
            Object.keys(compositeValue).forEach((subKey)=>{
                // Explicitly ignore __proto__ keys to avoid prototype pollution.
                // This must be done within an `if` block to keep the semgrep `prototype-pollution-assignment`
                // rule from triggering.
                if (subKey !== '__proto__') {
                    compositeValue[subKey] = replaceAliasInValue(compositeValue[subKey], oldPath, newPath);
                }
            });
            record[key] = compositeValue;
        } else if (key === '$value' || key === 'org.stripe.darkTheme') {
            // Simple value
            record[key] = replaceAliasInValue(record[key], oldPath, newPath);
        } else if (typeof record[key] === 'object') {
            replaceAliases(record[key], oldPath, newPath);
        }
    });
}
// StyleDictionary doesn't support tokens that are nested under other tokens which already hav a value.
// This function unnests the subtokens, transforming the token path to use underscores instead of dots.
function styleDictionaryUnnestSubtokens(currNode, path = null, rootNode = null) {
    const isNotReservedKey = (key)=>!key.startsWith('$');
    Object.keys(currNode).filter(isNotReservedKey).forEach((key)=>{
        if (typeof currNode[key] !== 'object') {
            return;
        }
        const property = currNode[key];
        styleDictionaryUnnestSubtokens(property, path == null ? key : `${path}.${key}`, rootNode !== null && rootNode !== void 0 ? rootNode : currNode);
        if ('$value' in property) {
            Object.keys(property).filter(isNotReservedKey).forEach((subKey)=>{
                currNode[`${key}_${subKey}`] = property[subKey];
                delete property[subKey];
                // replace values that reference `*.key.subKey` with `*.key_subKey`
                let oldPath = `${key}.${subKey}`;
                let newPath = `${key}_${subKey}`;
                if (path) {
                    oldPath = `${path}.${oldPath}`;
                    newPath = `${path}.${newPath}`;
                }
                replaceAliases(rootNode !== null && rootNode !== void 0 ? rootNode : currNode, oldPath, newPath);
            });
        }
        currNode[key] = property;
    });
}
/*
 Modifies the W3C token format to be compatible with StyleDictionary.
 Specifically:
 - Converts the `$value` key to `value`
 - Unnests subtokens, updating their path to use underscores.
 */ export function toStyleDictionaryFormat(tokens, overrideMap) {
    const result = toW3CTokenFormat(tokens, overrideMap);
    styleDictionaryUnnestSubtokens(result);
    styleDictionaryTransformValueKey(result);
    return result;
}

//# sourceMappingURL=exportTokens.js.map